import { detectFileFormat } from '@/features/datasets/utils/filePreviewUtils';
import {
    fetchFileColumns,
    readDatasetFileContent,
    MAX_FILE_CHUNK_SIZE,
} from '@/features/datasets/utils/datasetUploadFlow';
import {
    partitionEntriesByColumnOverlap,
    shouldAbortUploadForNoSharedColumns,
    showNoSharedColumnsToast,
} from '@/features/datasets/utils/sharedFileColumns';

function uploadNameFromFile(file) {
    const name = String(file?.name || '');
    const withoutExt = name.replace(/\.[^.]+$/, '');
    return withoutExt || name;
}

/**
 * Read columns for a dropped/picked File batch and decide whether the Upload
 * Dataset modal should open. `unknown` / fetch failures do not abort — the
 * modal re-checks once columns load.
 */
export async function inspectDroppedFilesColumnOverlap(files, { clientId } = {}) {
    const incoming = Array.from(files || []).filter(Boolean);
    if (incoming.length < 2) return { abort: false };

    const entries = await Promise.all(
        incoming.map(async (file) => {
            const format = detectFileFormat(file);
            try {
                const content = await readDatasetFileContent(file, {
                    chunkOnly: file.size > MAX_FILE_CHUNK_SIZE,
                });
                const columns = await fetchFileColumns({
                    filename: file.name,
                    clientId,
                    fileContent: content,
                    fileType: format,
                });
                return { file, uploadName: uploadNameFromFile(file), columns };
            } catch {
                return { file, uploadName: uploadNameFromFile(file), columns: [] };
            }
        })
    );

    if (!shouldAbortUploadForNoSharedColumns(entries)) {
        return { abort: false };
    }
    const { rejected } = partitionEntriesByColumnOverlap(entries);
    return { abort: true, rejected: rejected.length ? rejected : entries };
}

/**
 * Data Overview drop / OS picker: toast and skip the modal when the batch
 * has no shared columns. Returns whether the caller should open the modal.
 */
export async function gateDroppedFilesForUploadModal(files, { clientId, source } = {}) {
    if (source && source !== 'upload') return true;
    const result = await inspectDroppedFilesColumnOverlap(files, { clientId });
    if (!result.abort) return true;
    showNoSharedColumnsToast(result.rejected);
    return false;
}
